import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Modal, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.NODE_ENV === 'production' 
    ? 'https://flujo-app-backend.onrender.com' 
    : 'http://localhost:8000';

const GlobalWalletPage = () => {
    const [currencies, setCurrencies] = useState([]);
    const [balances, setBalances] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [filterCurrency, setFilterCurrency] = useState(null);

    // Modals State
    const [showIncomeModal, setShowIncomeModal] = useState(false);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [showCurrencyModal, setShowCurrencyModal] = useState(false);

    // Forms State
    const [txForm, setTxForm] = useState({ currency_id: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
    const [convForm, setConvForm] = useState({ from_id: '', to_id: '', amount_out: '', amount_in: '', description: '', date: new Date().toISOString().split('T')[0] });
    const [currForm, setCurrForm] = useState({ code: '', name: '', symbol: '$', initial_balance: '' });

    const loadData = useCallback(async () => {
        try {
            const resCurrencies = await axios.get(`${API_URL}/global-wallet/currencies`);
            const resBalances = await axios.get(`${API_URL}/global-wallet/balances`);
            
            const txPromises = resCurrencies.data.map(c => axios.get(`${API_URL}/global-wallet/transactions/${c.id}`));
            const txResponses = await Promise.all(txPromises);
            const allTxs = txResponses.flatMap(r => r.data);
            
            setCurrencies(resCurrencies.data);
            setBalances(resBalances.data);
            setTransactions(allTxs);

            if (!filterCurrency) {
                const clp = resCurrencies.data.find(c => c.code === 'CLP');
                if (clp) setFilterCurrency(clp.id);
                else if (resCurrencies.data.length > 0) setFilterCurrency(resCurrencies.data[0].id);
            }
        } catch (error) {
            console.error("Error loading wallet data", error);
        }
    }, [filterCurrency]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleIncome = async () => {
        try {
            await axios.post(`${API_URL}/global-wallet/transactions`, {
                ...txForm, type: 'income', amount: parseFloat(txForm.amount)
            });
            toast.success("Ingreso registrado");
            setShowIncomeModal(false);
            loadData();
        } catch (e) { toast.error("Error"); }
    };

    const handleExpense = async () => {
        try {
            await axios.post(`${API_URL}/global-wallet/transactions`, {
                ...txForm, type: 'expense', amount: parseFloat(txForm.amount)
            });
            toast.success("Gasto registrado");
            setShowExpenseModal(false);
            loadData();
        } catch (e) { toast.error("Error"); }
    };

    const handleConvert = async () => {
        try {
            await axios.post(`${API_URL}/global-wallet/convert`, {
                from_currency_id: parseInt(convForm.from_id),
                to_currency_id: parseInt(convForm.to_id),
                amount_out: parseFloat(convForm.amount_out),
                amount_in: parseFloat(convForm.amount_in),
                date: convForm.date,
                description: convForm.description
            });
            toast.success("Conversión realizada");
            setShowConvertModal(false);
            loadData();
        } catch (e) { toast.error("Error"); }
    };

    const handleCreateCurrency = async () => {
        try {
            const res = await axios.post(`${API_URL}/global-wallet/currencies`, {
                code: currForm.code, name: currForm.name, symbol: currForm.symbol
            });
            if (currForm.initial_balance && parseFloat(currForm.initial_balance) > 0) {
                await axios.post(`${API_URL}/global-wallet/transactions`, {
                    currency_id: res.data.id, amount: parseFloat(currForm.initial_balance),
                    type: 'income', description: 'Saldo Inicial', date: new Date().toISOString()
                });
            }
            toast.success("Moneda creada");
            setShowCurrencyModal(false);
            setCurrForm({ code: '', name: '', symbol: '$', initial_balance: '' });
            loadData();
        } catch (e) { toast.error("Error"); }
    };

    const formatMoney = (amount, symbol) => {
        if (amount === undefined || amount === null) return '-';
        const val = parseFloat(amount);
        const numStr = val.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        const cleanSymbol = symbol ? symbol.replace(/[0-9]/g, '').trim() : '$';
        return `${cleanSymbol} ${numStr}`;
    };

    const getTxBadge = (type) => {
        const map = {
            'income': <Badge bg="success">Ingreso</Badge>,
            'expense': <Badge bg="danger">Gasto</Badge>,
            'exchange_out': <Badge bg="warning" text="dark">Conv. (S)</Badge>,
            'exchange_in': <Badge bg="info" text="dark">Conv. (E)</Badge>
        };
        return map[type] || <Badge bg="secondary">{type}</Badge>;
    };

    const processedTransactions = useMemo(() => {
        if (!filterCurrency) return [];
        const txs = transactions.filter(t => t.currency_id === filterCurrency);
        txs.sort((a, b) => new Date(a.date) - new Date(b.date) || a.id - b.id);
        let balance = 0;
        const withBalance = txs.map(t => {
            balance += t.amount;
            return { ...t, running_balance: balance };
        });
        return withBalance.reverse();
    }, [transactions, filterCurrency]);

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Global 66 - Billetera</h2>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => setShowCurrencyModal(true)}>⚙️ Monedas</Button>
                    <Link to="/" className="btn btn-secondary btn-sm">Volver</Link>
                </div>
            </div>

            <Card className="mb-4 shadow-sm border-0 bg-light">
                <Card.Body className="d-flex gap-3 justify-content-center flex-wrap">
                    <Button variant="success" onClick={() => setShowIncomeModal(true)}>⬇️ Cargar Saldo</Button>
                    <Button variant="primary" onClick={() => setShowConvertModal(true)}>🔄 Convertir Divisa</Button>
                    <Button variant="danger" onClick={() => setShowExpenseModal(true)}>⬆️ Registrar Gasto</Button>
                </Card.Body>
            </Card>

            <Row className="g-3 mb-4">
                {balances.map(b => (
                    <Col key={b.currency.id} md={3}>
                        <Card className={`h-100 shadow-sm text-center border-top border-4 ${filterCurrency === b.currency.id ? 'border-success bg-white' : 'border-primary'}`}>
                            <Card.Body>
                                <h6 className="text-muted small">{b.currency.name}</h6>
                                <h3 className="fw-bold my-2">{formatMoney(b.balance, b.currency.symbol)}</h3>
                                <Button variant={filterCurrency === b.currency.id ? "success" : "outline-primary"} size="sm" onClick={() => setFilterCurrency(b.currency.id)}>Ver Movimientos</Button>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {filterCurrency && (
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                        <span className="fw-bold">Movimientos: {currencies.find(c => c.id === filterCurrency)?.name}</span>
                    </Card.Header>
                    <Table hover responsive size="sm" className="mb-0">
                        <thead className="bg-light"><tr><th>Fecha</th><th>Tipo</th><th className="text-end">Monto</th><th className="text-end table-active">Saldo</th><th>Descripción</th><th className="text-end">Acciones</th></tr></thead>
                        <tbody>
                            {processedTransactions.map(t => {
                                const currency = currencies.find(c => c.id === t.currency_id);
                                return (
                                    <tr key={t.id}>
                                        <td>{new Date(t.date).toLocaleDateString()}</td>
                                        <td>{getTxBadge(t.type)}</td>
                                        <td className={`text-end fw-bold ${t.amount >= 0 ? 'text-success' : 'text-danger'}`}>{formatMoney(t.amount, currency?.symbol || '$')}</td>
                                        <td className="text-end fw-bold table-active">{formatMoney(t.running_balance, currency?.symbol || '$')}</td>
                                        <td>{t.description}</td>
                                        <td className="text-end">
                                            <Button variant="outline-danger" size="sm" className="py-0 px-2" onClick={async () => { if (window.confirm("¿Borrar?")) { await axios.delete(`${API_URL}/global-wallet/transactions/${t.id}`); loadData(); }}} >✕</Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Card>
            )}

            {/* MODALS */}
            <Modal show={showIncomeModal} onHide={() => setShowIncomeModal(false)}>
                <Modal.Header closeButton><Modal.Title>Cargar Saldo</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-2"><Form.Label>Moneda</Form.Label><Form.Select onChange={e => setTxForm({...txForm, currency_id: e.target.value})}><option value="">Seleccione...</option>{currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}</Form.Select></Form.Group>
                    <Form.Group className="mb-2"><Form.Label>Monto</Form.Label><Form.Control type="number" onChange={e => setTxForm({...txForm, amount: e.target.value})}/></Form.Group>
                    <Form.Group className="mb-2"><Form.Label>Descripción</Form.Label><Form.Control onChange={e => setTxForm({...txForm, description: e.target.value})}/></Form.Group>
                    <Form.Group><Form.Label>Fecha</Form.Label><Form.Control type="date" value={txForm.date} onChange={e => setTxForm({...txForm, date: e.target.value})}/></Form.Group>
                </Modal.Body>
                <Modal.Footer><Button onClick={handleIncome}>Guardar</Button></Modal.Footer>
            </Modal>

            <Modal show={showExpenseModal} onHide={() => setShowExpenseModal(false)}>
                <Modal.Header closeButton><Modal.Title>Registrar Gasto</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-2"><Form.Label>Moneda</Form.Label><Form.Select onChange={e => setTxForm({...txForm, currency_id: e.target.value})}><option value="">Seleccione...</option>{currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}</Form.Select></Form.Group>
                    <Form.Group className="mb-2"><Form.Label>Monto</Form.Label><Form.Control type="number" onChange={e => setTxForm({...txForm, amount: e.target.value})}/></Form.Group>
                    <Form.Group className="mb-2"><Form.Label>Descripción</Form.Label><Form.Control onChange={e => setTxForm({...txForm, description: e.target.value})}/></Form.Group>
                    <Form.Group><Form.Label>Fecha</Form.Label><Form.Control type="date" value={txForm.date} onChange={e => setTxForm({...txForm, date: e.target.value})}/></Form.Group>
                </Modal.Body>
                <Modal.Footer><Button variant="danger" onClick={handleExpense}>Registrar</Button></Modal.Footer>
            </Modal>

            <Modal show={showConvertModal} onHide={() => setShowConvertModal(false)} size="lg">
                <Modal.Header closeButton><Modal.Title>Convertir Divisa</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}><h6>Sale</h6><Form.Select className="mb-2" onChange={e => setConvForm({...convForm, from_id: e.target.value})}><option value="">Moneda...</option>{currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}</Form.Select><Form.Control type="number" placeholder="Monto" onChange={e => setConvForm({...convForm, amount_out: e.target.value})}/></Col>
                        <Col md={6}><h6>Entra</h6><Form.Select className="mb-2" onChange={e => setConvForm({...convForm, to_id: e.target.value})}><option value="">Moneda...</option>{currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}</Form.Select><Form.Control type="number" placeholder="Monto" onChange={e => setConvForm({...convForm, amount_in: e.target.value})}/></Col>
                        <Col md={12} className="mt-3"><Form.Label>Nota</Form.Label><Form.Control placeholder="Ej: Cambio a USD" onChange={e => setConvForm({...convForm, description: e.target.value})}/></Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer><Button onClick={handleConvert}>Confirmar</Button></Modal.Footer>
            </Modal>

            <Modal show={showCurrencyModal} onHide={() => setShowCurrencyModal(false)} size="sm">
                <Modal.Header closeButton><Modal.Title>Nueva Moneda</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Control className="mb-2" placeholder="Código (ej. ARS)" onChange={e => setCurrForm({...currForm, code: e.target.value.toUpperCase()})} />
                    <Form.Control className="mb-2" placeholder="Nombre" onChange={e => setCurrForm({...currForm, name: e.target.value})} />
                    <Form.Control className="mb-2" placeholder="Símbolo" value={currForm.symbol} onChange={e => setCurrForm({...currForm, symbol: e.target.value})} />
                    <Form.Control type="number" placeholder="Saldo Inicial" onChange={e => setCurrForm({...currForm, initial_balance: e.target.value})} />
                </Modal.Body>
                <Modal.Footer><Button onClick={handleCreateCurrency}>Crear</Button></Modal.Footer>
            </Modal>
        </Container>
    );
};

export default GlobalWalletPage;